import React, { useState, useEffect, useCallback } from 'react';
import './AdminProgramManagement.css'; // Reuse existing styles
import config from '../config';

const { API_URL } = config;

const AdminAssessmentManagement = ({ user, onBack, onMessage }) => {
  const [assessmentForm, setAssessmentForm] = useState({
    name: '', totalQuestions: 30, timeLimit: 60, distribution: {}
  });
  const [allAssessments, setAllAssessments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [assessmentsLoading, setAssessmentsLoading] = useState(false);
  const [selectedAssessmentForEdit, setSelectedAssessmentForEdit] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [allQuestions, setAllQuestions] = useState([]);

  const loadAssessmentConfigs = useCallback(async () => {
    setAssessmentsLoading(true);
    
    try {
      // Demo mode - provide demo assessments
      if (user?.token === 'demo-token-for-local-testing') {
        const demoAssessments = [
          {
            name: 'Cloud Operator Demo',
            totalQuestions: 20,
            timeLimit: 30,
            distribution: { 'AWS': 40, 'DevOps': 35, 'Security': 25 }
          },
          {
            name: 'DevOps Fundamentals Demo',
            totalQuestions: 25,
            timeLimit: 45,
            distribution: { 'CI/CD': 50, 'Monitoring': 30, 'Infrastructure': 20 }
          }
        ];
        setAllAssessments(demoAssessments);
        setAssessmentsLoading(false);
        return;
      }

      const [assessmentsResponse, questionsResponse] = await Promise.all([
        fetch(`${API_URL}/admin/questions?type=assessments`, {
          headers: {
            ...(user?.token ? { 'Authorization': `Bearer ${user.token}` } : {})
          }
        }),
        fetch(`${API_URL}/admin/questions`, {
          headers: {
            ...(user?.token ? { 'Authorization': `Bearer ${user.token}` } : {})
          }
        })
      ]);
      
      if (!assessmentsResponse.ok) throw new Error(`HTTP ${assessmentsResponse.status}`);
      if (!questionsResponse.ok) throw new Error(`HTTP ${questionsResponse.status}`);
      
      const assessmentsData = await assessmentsResponse.json();
      const questionsData = await questionsResponse.json();
      
      setAllAssessments(assessmentsData || []);
      setAllQuestions(questionsData || []);
    } catch (error) {
      console.error('Error loading assessments:', error);
      onMessage({ text: 'Error loading assessments: ' + error.message, type: 'error' });
      setAllAssessments([]);
    } finally {
      setAssessmentsLoading(false);
    }
  }, [user?.token, onMessage]);

  const loadCategories = useCallback((assessmentName, questions) => {
    if (!assessmentName || !questions) {
      setCategories([]);
      return;
    }
    const assessmentCategories = [...new Set(questions
      .filter(q => q.assessment_name === assessmentName)
      .map(q => q.category)
      .filter(cat => cat))];
    setCategories(assessmentCategories);
  }, []);

  // Load categories when assessment name changes (only for new assessments)
  useEffect(() => {
    if (assessmentForm.name && !selectedAssessmentForEdit) {
      loadCategories(assessmentForm.name, allQuestions);
    }
  }, [assessmentForm.name, allQuestions, loadCategories, selectedAssessmentForEdit]);

  // Load data only once on mount
  useEffect(() => {
    loadAssessmentConfigs();
  }, [loadAssessmentConfigs]);

  const handleAssessmentSelect = (assessmentName) => {
    const assessment = allAssessments.find(a => a.name === assessmentName);
    if (assessment) {
      // Load categories from both the assessment config and existing questions
      const categoriesFromQuestions = [...new Set(allQuestions
        .filter(q => q.assessment_name === assessmentName)
        .map(q => q.category)
        .filter(cat => cat))];
      
      const categoriesFromDistribution = Object.keys(assessment.distribution || {});
      
      // Combine and deduplicate
      const allCategories = [...new Set([...categoriesFromQuestions, ...categoriesFromDistribution])];
      
      // Set categories first
      setCategories(allCategories);
      
      // Then set form with distribution
      setAssessmentForm({
        name: assessment.name,
        totalQuestions: assessment.totalQuestions || 30,
        timeLimit: assessment.timeLimit || 60,
        distribution: assessment.distribution || {}
      });
      
      // Auto-open category manager when editing
      setShowCategoryManager(true);
    } else {
      setAssessmentForm({
        name: assessmentName,
        totalQuestions: 30,
        timeLimit: 60,
        distribution: {}
      });
      loadCategories(assessmentName, allQuestions);
    }
    setSelectedAssessmentForEdit(assessmentName);
  };

  const handleSaveAssessment = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!assessmentForm.name.trim()) {
      onMessage({ text: 'Assessment name is required', type: 'error' });
      return;
    }
    
    if (categories.length === 0) {
      onMessage({ text: 'Please add at least one category before saving the assessment.', type: 'error' });
      return;
    }
    
    const totalPercentage = Object.values(assessmentForm.distribution).reduce((sum, val) => sum + (parseInt(val) || 0), 0);
    if (totalPercentage !== 100) {
      onMessage({ text: 'Total percentage must equal 100%', type: 'error' });
      return;
    }

    try {
      // Demo mode - simulate save
      if (user?.token === 'demo-token-for-local-testing') {
        onMessage({ text: `Demo: Assessment "${assessmentForm.name}" saved successfully! In production, this would be saved to the database.`, type: 'success' });
        loadAssessmentConfigs();
        setAssessmentForm({ name: '', totalQuestions: 30, timeLimit: 60, distribution: {} });
        setSelectedAssessmentForEdit('');
        setCategories([]);
        return;
      }

      const assessmentData = {
        type: 'assessment',
        assessment_name: assessmentForm.name.trim(),
        totalQuestions: parseInt(assessmentForm.totalQuestions),
        timeLimit: parseInt(assessmentForm.timeLimit),
        distribution: assessmentForm.distribution
      };
      
      const response = await fetch(`${API_URL}/admin/questions`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(user?.token ? { 'Authorization': `Bearer ${user.token}` } : {})
        },
        body: JSON.stringify(assessmentData)
      });
      
      const result = await response.json();
      
      if (response.ok) {
        onMessage({ text: 'Assessment saved successfully!', type: 'success' });
        setAssessmentForm({ name: '', totalQuestions: 30, timeLimit: 60, distribution: {} });
        setSelectedAssessmentForEdit('');
        setCategories([]);
        loadAssessmentConfigs();
      } else {
        const errorMsg = result.error || result.message || 'Unknown error occurred';
        onMessage({ text: 'Error: ' + errorMsg, type: 'error' });
      }
    } catch (error) {
      console.error('Assessment save exception:', error);
      onMessage({ text: 'Error: ' + error.message, type: 'error' });
    }
  };

  const addCategory = () => {
    if (!newCategoryName.trim() || categories.includes(newCategoryName.trim())) return;
    const newCategory = newCategoryName.trim();
    setCategories([...categories, newCategory]);
    
    // Auto-initialize distribution for new category
    if (!assessmentForm.distribution[newCategory]) {
      setAssessmentForm({
        ...assessmentForm,
        distribution: {
          ...assessmentForm.distribution,
          [newCategory]: 0
        }
      });
    }
    
    setNewCategoryName('');
  };

  const removeCategory = (categoryToRemove) => {
    if (!window.confirm(`Remove category "${categoryToRemove}"? This will affect existing questions.`)) return;
    setCategories(categories.filter(cat => cat !== categoryToRemove));
    const newDistribution = {...assessmentForm.distribution};
    delete newDistribution[categoryToRemove];
    setAssessmentForm({...assessmentForm, distribution: newDistribution});
  };

  return (
    <div className="admin-program-management">
      {/* Demo Mode Notice */}
      {user?.token === 'demo-token-for-local-testing' && (
        <div className="card" style={{background: '#d1ecf1', border: '1px solid #bee5eb'}}>
          <h4 style={{color: '#0c5460'}}>🔧 Demo Mode Active</h4>
          <p style={{color: '#0c5460', margin: '10px 0'}}>
            You're viewing demo assessment data. In production, this would connect to your actual assessment database.
          </p>
        </div>
      )}
      
      <div className="card">
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <h3>Create/Update Assessment</h3>
          <button type="button" className="btn btn-secondary" onClick={() => setShowCategoryManager(!showCategoryManager)}>
            {showCategoryManager ? 'Hide' : 'Manage'} Categories
          </button>
        </div>
        
        {showCategoryManager && (
          <div className="category-manager" style={{border: '1px solid #ddd', padding: '15px', marginBottom: '20px', borderRadius: '5px', background: '#f8f9fa'}}>
            <h4>Category Management</h4>
            <p style={{fontSize: '0.9em', color: '#666', marginBottom: '15px'}}>
              Add or remove categories for this assessment. Categories help organize questions by topic.
            </p>
            
            {/* Create new category */}
            <div style={{marginBottom: '15px'}}>
              <strong style={{fontSize: '0.9em', display: 'block', marginBottom: '8px'}}>Create New Category:</strong>
              <div style={{display: 'flex', gap: '10px'}}>
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addCategory();
                    }
                  }}
                  placeholder="Type new category name"
                  className="form-control"
                  style={{flex: 1}}
                />
                <button 
                  type="button" 
                  onClick={addCategory} 
                  className="btn btn-primary"
                  disabled={!newCategoryName.trim() || categories.includes(newCategoryName.trim())}
                >
                  Create & Add
                </button>
              </div>
            </div>
            
            <div className="categories-list">
              <strong>Current Categories ({categories.filter(cat => (assessmentForm.distribution[cat] || 0) > 0).length}):</strong>
              {categories.filter(cat => (assessmentForm.distribution[cat] || 0) > 0).length === 0 ? (
                <p style={{color: '#666', fontSize: '0.9em', fontStyle: 'italic', marginTop: '10px'}}>
                  No categories yet. Add your first category above.
                </p>
              ) : (
                <div style={{display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '10px'}}>
                  {categories
                    .filter(category => (assessmentForm.distribution[category] || 0) > 0)
                    .map(category => {
                      const distributionPercent = assessmentForm.distribution[category] || 0;
                      return (
                        <div 
                          key={category} 
                          style={{
                            display: 'flex', 
                            alignItems: 'center', 
                            background: 'white', 
                            padding: '8px 12px', 
                            borderRadius: '5px',
                            border: '1px solid #dee2e6',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                          }}
                        >
                          <span style={{fontWeight: '500'}}>{category}</span>
                          <span style={{
                            marginLeft: '8px', 
                            fontSize: '0.85em', 
                            color: '#007bff',
                            background: '#e7f3ff',
                            padding: '2px 6px',
                            borderRadius: '3px',
                            fontWeight: 'bold'
                          }}>
                            {distributionPercent}%
                          </span>
                          <button 
                            type="button" 
                            onClick={() => removeCategory(category)}
                            style={{
                              marginLeft: '10px', 
                              background: 'none', 
                              border: 'none', 
                              color: '#dc3545', 
                              cursor: 'pointer',
                              fontSize: '18px',
                              fontWeight: 'bold',
                              padding: '0 5px'
                            }}
                            title="Remove category"
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        )}
        
        <form onSubmit={handleSaveAssessment}>
          <div className="form-group">
            <label>Assessment Name:</label>
            <div style={{display: 'flex', gap: '10px'}}>
              <select
                value={selectedAssessmentForEdit}
                onChange={(e) => handleAssessmentSelect(e.target.value)}
                style={{flex: 1}}
              >
                <option value="">Select existing or create new...</option>
                {allAssessments.map(assessment => (
                  <option key={assessment.name} value={assessment.name}>{assessment.name}</option>
                ))}
              </select>
              <input
                type="text"
                value={assessmentForm.name}
                onChange={(e) => {
                  setAssessmentForm({...assessmentForm, name: e.target.value});
                  setSelectedAssessmentForEdit('');
                }}
                placeholder="Or type new assessment name"
                style={{flex: 1}}
                required
              />
            </div>
          </div>
          
          <div className="form-group">
            <label>Total Questions:</label>
            <input
              type="number"
              value={assessmentForm.totalQuestions}
              onChange={(e) => setAssessmentForm({...assessmentForm, totalQuestions: e.target.value})}
              min="10"
              max="100"
              required
            />
          </div>
          
          <div className="form-group">
            <label>Time Limit (minutes):</label>
            <input
              type="number"
              value={assessmentForm.timeLimit}
              onChange={(e) => setAssessmentForm({...assessmentForm, timeLimit: e.target.value})}
              min="15"
              max="180"
              required
            />
          </div>
          
          <div className="form-group">
            <label>Category Distribution (%):</label>
            {categories.length === 0 ? (
              <p style={{color: '#666', fontSize: '0.9em', fontStyle: 'italic'}}>
                No categories available. Please add categories using the "Manage Categories" section above.
              </p>
            ) : (
              <div style={{border: '1px solid #ddd', padding: '15px', borderRadius: '5px', background: '#f8f9fa'}}>
                <div style={{marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <strong>Set percentage for each category:</strong>
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={() => {
                      const equalPercentage = Math.floor(100 / categories.length);
                      const remainder = 100 - (equalPercentage * categories.length);
                      const newDistribution = {};
                      categories.forEach((cat, idx) => {
                        newDistribution[cat] = equalPercentage + (idx === 0 ? remainder : 0);
                      });
                      setAssessmentForm({...assessmentForm, distribution: newDistribution});
                    }}
                  >
                    Distribute Equally
                  </button>
                </div>
                
                {categories.map(category => {
                  const percentage = assessmentForm.distribution[category] || 0;
                  const questionCount = Math.round((percentage / 100) * assessmentForm.totalQuestions);
                  return (
                    <div key={category} style={{
                      display: 'flex', 
                      alignItems: 'center', 
                      margin: '10px 0',
                      padding: '10px',
                      background: 'white',
                      borderRadius: '5px',
                      border: '1px solid #dee2e6'
                    }}>
                      <label style={{minWidth: '150px', fontWeight: 'bold', marginBottom: 0}}>{category}:</label>
                      <input
                        type="range"
                        value={percentage}
                        onChange={(e) => {
                          const newPercentage = parseInt(e.target.value);
                          const newDistribution = {
                            ...assessmentForm.distribution,
                            [category]: newPercentage
                          };
                          
                          // Remove category from list if set to 0
                          if (newPercentage === 0) {
                            setCategories(categories.filter(cat => cat !== category));
                            delete newDistribution[category];
                          }
                          
                          setAssessmentForm({
                            ...assessmentForm,
                            distribution: newDistribution
                          });
                        }}
                        min="0"
                        max="100"
                        step="5"
                        style={{flex: 1, margin: '0 15px'}}
                      />
                      <input
                        type="number"
                        value={percentage}
                        onChange={(e) => {
                          const newPercentage = parseInt(e.target.value) || 0;
                          const newDistribution = {
                            ...assessmentForm.distribution,
                            [category]: newPercentage
                          };
                          
                          // Remove category from list if set to 0
                          if (newPercentage === 0) {
                            setCategories(categories.filter(cat => cat !== category));
                            delete newDistribution[category];
                          }
                          
                          setAssessmentForm({
                            ...assessmentForm,
                            distribution: newDistribution
                          });
                        }}
                        min="0"
                        max="100"
                        style={{width: '70px', textAlign: 'center', marginRight: '5px'}}
                        required
                      />
                      <span style={{minWidth: '100px', fontSize: '0.9em', color: '#666'}}>
                        ({questionCount} {questionCount === 1 ? 'question' : 'questions'})
                      </span>
                    </div>
                  );
                })}
                
                <div style={{
                  marginTop: '15px', 
                  padding: '10px',
                  background: Object.values(assessmentForm.distribution).reduce((sum, val) => sum + (parseInt(val) || 0), 0) === 100 ? '#d4edda' : '#f8d7da',
                  borderRadius: '5px',
                  fontWeight: 'bold',
                  textAlign: 'center'
                }}>
                  Total: {Object.values(assessmentForm.distribution).reduce((sum, val) => sum + (parseInt(val) || 0), 0)}%
                  {Object.values(assessmentForm.distribution).reduce((sum, val) => sum + (parseInt(val) || 0), 0) !== 100 && (
                    <span style={{color: '#721c24', marginLeft: '10px'}}>⚠ Must equal 100%</span>
                  )}
                  {Object.values(assessmentForm.distribution).reduce((sum, val) => sum + (parseInt(val) || 0), 0) === 100 && (
                    <span style={{color: '#155724', marginLeft: '10px'}}>✓ Perfect!</span>
                  )}
                </div>
              </div>
            )}
          </div>
          
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={categories.length === 0 || Object.values(assessmentForm.distribution).reduce((sum, val) => sum + (parseInt(val) || 0), 0) !== 100}
          >
            Save Assessment
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => {
            setAssessmentForm({ name: '', totalQuestions: 30, timeLimit: 60, distribution: {} });
            setSelectedAssessmentForEdit('');
            setCategories([]);
          }}>Clear Form</button>
        </form>
      </div>

      <div className="card">
        <h3>Existing Assessments</h3>
        <button className="btn btn-secondary" onClick={loadAssessmentConfigs} disabled={assessmentsLoading}>
          {assessmentsLoading ? 'Loading...' : 'Refresh Assessments'}
        </button>
        
        {assessmentsLoading && <div className="loading">Loading assessments...</div>}
        
        {!assessmentsLoading && (
          <div className="assessments-list">
            {allAssessments.length === 0 ? (
              <p>No assessments configured.</p>
            ) : (
              allAssessments.map(assessment => (
                <div key={assessment.name} className="program-item">
                  <div className="program-header">
                    <div>
                      <h4>{assessment.name}</h4>
                      <p><strong>Questions:</strong> {assessment.totalQuestions}</p>
                      <p><strong>Time Limit:</strong> {assessment.timeLimit} minutes</p>
                      <p><strong>Distribution:</strong> {Object.entries(assessment.distribution || {}).map(([cat, pct]) => `${cat}: ${pct}%`).join(', ')}</p>
                    </div>
                    <div className="program-actions">
                      <button className="btn btn-secondary" onClick={() => {
                        handleAssessmentSelect(assessment.name);
                      }}>Edit Settings</button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAssessmentManagement;